import React, { useCallback, useEffect, useMemo } from 'react';

import type { ChatHistory } from '~/components/AiChat/controller/types';
import { useChatHistoryEntries } from '~/components/AiChat/hooks/useChatHistoryEntries';
import { groupHistoryByRelativeTime } from '~/components/AiChat/utils/groupHistoryByRelativeTime';
import { Text } from '~/components/core';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';

import { ListChatHistoryItem } from './FullScreenChatHistoryItem';

interface FullScreenChatHistoryListProps {
  history: ChatHistory;
  /** The title of the conversation on screen (`history.currentId`), which its entry follows. */
  currentTitle: string | null;
  onChatSelect: (chatId: string) => Promise<void>;
  isExpanded: boolean;
}

export const FullScreenChatHistoryList: React.FC<FullScreenChatHistoryListProps> = ({
  history,
  currentTitle,
  onChatSelect,
  isExpanded,
}) => {
  const { entries, setEntries, isLoading, isError } = useChatHistoryEntries(history);
  const { currentId, untitledTitle } = history;

  useEffect(() => {
    if (!currentId || !currentTitle) return;
    setEntries((prev) => prev.map((item) => (item.id === currentId ? { ...item, title: currentTitle } : item)));
  }, [currentId, currentTitle, setEntries]);

  const handleChatSelect = useCallback(
    async (chatId: string) => {
      await onChatSelect(chatId);
    },
    [onChatSelect]
  );

  const groupedHistory = useMemo(() => groupHistoryByRelativeTime(entries), [entries]);

  if (!isExpanded) {
    return null;
  }

  return (
    <div className="mt-20 flex flex-col gap-16 px-16">
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {groupedHistory.map(({ groupLabel, items }) => (
          <div key={groupLabel} className="flex flex-col">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="px-12">
              {groupLabel}
            </Text>

            {items.map((chat) => (
              <ListChatHistoryItem
                key={chat.id}
                displayTitle={chat.title || untitledTitle(chat.id)}
                onClick={() => handleChatSelect(chat.id)}
              />
            ))}
          </div>
        ))}
      </LoadingSwitch>
    </div>
  );
};
